import { motion } from "framer-motion";
import { Heart, Users, HandHeart, Handshake, Shield, Lightbulb, RefreshCw, Leaf } from "lucide-react";
import Navigation from "../../components/Navigation";
import PageHero from "../../components/PageHero";

// Animation constants
const FADE_IN_UP = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const CARD_ANIMATION_DELAY = 0.1;

// Values Page
const ValuesPage = () => {
  const values = [
    {
      icon: Heart,
      title: "Inclusivity",
      description: "All people are worthy of dignity and respect, regardless of age, race, ethnicity, national origin, color, sex, gender, sexual orientation, disability, religion, or circumstances.",
      image: "https://customer-assets.emergentagent.com/job_7b46b431-509a-4162-84d5-ce52c765da4c/artifacts/s1ndz18g_1%20inclusivity.jpg"
    },
    {
      icon: HandHeart,
      title: "Intersectionality",
      description: "Food can be used as an entry point to a Multi-Tiered System of Support (MTSS) that addresses the social determinants of health.",
      image: "https://customer-assets.emergentagent.com/job_7b46b431-509a-4162-84d5-ce52c765da4c/artifacts/rifhpneh_2%20intersectionality.jpg"
    },
    {
      icon: Shield,
      title: "Trust",
      description: "People understand their own needs best and can make their own choices.",
      image: "https://customer-assets.emergentagent.com/job_7b46b431-509a-4162-84d5-ce52c765da4c/artifacts/yino7r6f_3%20trust.jpg"
    },
    {
      icon: Lightbulb,
      title: "Individual Empowerment",
      description: "Knowledge and resources help people make informed choices.",
      image: "https://customer-assets.emergentagent.com/job_7b46b431-509a-4162-84d5-ce52c765da4c/artifacts/0mt72fm0_4%20empowerment.jpg"
    },
    {
      icon: Users,
      title: "Community Empowerment",
      description: "People with lived experience are best equipped to help others in the same situation.",
      image: "https://customer-assets.emergentagent.com/job_7b46b431-509a-4162-84d5-ce52c765da4c/artifacts/ax7thu1t_5%20community%20empowerment.jpg"
    },
    {
      icon: Handshake,
      title: "Collaboration",
      description: "Sharing the work with other organizations is more efficient and effective than working alone.",
      image: "https://customer-assets.emergentagent.com/job_7b46b431-509a-4162-84d5-ce52c765da4c/artifacts/f19sb5sb_6%20collaboration.jpg"
    },
    {
      icon: RefreshCw,
      title: "Adaptability",
      description: "Organizations must be responsive to changing community needs, cultural practices, and external factors in order to succeed.",
      image: "https://customer-assets.emergentagent.com/job_7b46b431-509a-4162-84d5-ce52c765da4c/artifacts/122hhip4_7%20adaptability.jpg"
    },
    {
      icon: Leaf,
      title: "Sustainability",
      description: "Solutions must be sustainable in order to be successful.",
      image: "https://customer-assets.emergentagent.com/job_7b46b431-509a-4162-84d5-ce52c765da4c/artifacts/92w9toyk_8%20sustainability.jpg"
    }
  ];

  return (
    <div data-testid="values-page" className="min-h-screen bg-[#FAFAFA]">
      <Navigation />
      
      <PageHero 
        image="https://customer-assets.emergentagent.com/job_7b46b431-509a-4162-84d5-ce52c765da4c/artifacts/xs5s1w5k_Values%20header.jpg"
        alt="Values"
        objectPosition="center 35%"
      />

      {/* Values Content */}
      <section className="py-12 md:py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={FADE_IN_UP.initial}
            animate={FADE_IN_UP.animate}
            transition={FADE_IN_UP.transition}
            className="text-center mb-12"
          >
            <h1 
              data-testid="values-header"
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              We Believe In...
            </h1>
            <div className="w-24 h-1 bg-[#C41E3A] mx-auto rounded-full"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * CARD_ANIMATION_DELAY }}
                  data-testid={`value-card-${value.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="value-card bg-white rounded-xl overflow-hidden border border-gray-100"
                >
                  <div className="h-64 overflow-hidden">
                    <img 
                      src={value.image} 
                      alt={value.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-full bg-[#C41E3A]/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="text-[#C41E3A]" size={24} />
                      </div>
                      <h3 
                        className="font-bold text-[#1A1A1A] text-xl"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                      >
                        {value.title}
                      </h3>
                    </div>
                    <p className="text-[#666666] text-lg leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ValuesPage;
